import * as FiIcons from 'react-icons/fi';

const { FiUser, FiUsers, FiActivity, FiCompass, FiHeart } = FiIcons;

// Static class strings so Tailwind can always detect them
const SCENARIO_STYLES = {
  blue: {
    icon: 'bg-blue-100 text-blue-600',
    hover: 'hover:border-blue-200'
  },
  emerald: {
    icon: 'bg-emerald-100 text-emerald-600',
    hover: 'hover:border-emerald-200'
  },
  purple: {
    icon: 'bg-purple-100 text-purple-600',
    hover: 'hover:border-purple-200'
  },
  orange: {
    icon: 'bg-orange-100 text-orange-600',
    hover: 'hover:border-orange-200'
  },
  rose: {
    icon: 'bg-rose-100 text-rose-600',
    hover: 'hover:border-rose-200'
  }
};

const BASE_SCENARIOS = [
  {
    id: 'young_pro',
    name: 'Young Professional',
    icon: FiUser,
    color: 'blue',
    desc: 'Single, renting, early career',
    payload: {
      goals: {
        currentAge: 25,
        targetAge: 60,
        expectedROI: 8,
        startingNetWorth: 15000
      },
      incomeStreams: [
        { id: 1, name: 'Salary', amount: 5500, frequency: 'monthly', accountType: 'income_hub' },
        { id: 2, name: 'Side Hustle', amount: 500, frequency: 'monthly', accountType: 'income_hub' }
      ],
      expenses: [
        { id: 1, name: 'Rent & Utilities', amount: 1800, frequency: 'monthly', accountType: 'bills' },
        { id: 2, name: 'Student Loan', amount: 400, frequency: 'monthly', accountType: 'bills' },
        { id: 3, name: 'Groceries/Food', amount: 600, frequency: 'monthly', accountType: 'bills' },
        { id: 4, name: 'Transportation', amount: 300, frequency: 'monthly', accountType: 'bills' },
        { id: 5, name: 'Discretionary', amount: 800, frequency: 'monthly', accountType: 'discretionary' }
      ],
      investmentBuckets: [
        { id: 1, name: '401k Match', amount: 300, frequency: 'monthly', icon: 'Briefcase', color: 'slate', accountType: 'investing_transfer' },
        { id: 2, name: 'Roth IRA', amount: 500, frequency: 'monthly', icon: 'TrendingUp', color: 'blue', accountType: 'investing_transfer' },
        { id: 3, name: 'Emergency Fund', amount: 300, frequency: 'monthly', icon: 'Shield', color: 'indigo', accountType: 'emergency' }
      ]
    }
  },
  {
    id: 'newly_married',
    name: 'Newly Married',
    icon: FiHeart,
    color: 'rose',
    desc: 'Newlyweds, new mortgage, dual income',
    payload: {
      goals: {
        currentAge: 32,
        targetAge: 45,
        expectedROI: 7,
        startingNetWorth: 1000
      },
      incomeStreams: [
        { id: 1, name: 'W2', amount: 4481, frequency: 'biweekly', accountType: 'income_hub' },
        { id: 2, name: 'Website', amount: 100, frequency: 'biweekly', accountType: 'income_hub' },
        { id: 3, name: 'Caregiving', amount: 600, frequency: 'biweekly', accountType: 'income_hub' }
      ],
      expenses: [
        { id: 1, name: 'Tithe', amount: 626, frequency: 'biweekly', accountType: 'bills' },
        { id: 2, name: 'Bills', amount: 170, frequency: 'biweekly', accountType: 'bills' },
        { id: 3, name: 'Food', amount: 240, frequency: 'weekly', accountType: 'bills' },
        { id: 4, name: 'Relationship', amount: 240, frequency: 'weekly', accountType: 'discretionary' },
        { id: 5, name: 'Spouse Fund', amount: 180, frequency: 'weekly', accountType: 'spouse_freedom' },
        { id: 6, name: 'Gas', amount: 50, frequency: 'weekly', accountType: 'bills' },
        { id: 7, name: 'Mortgage', amount: 2165, frequency: 'biweekly', accountType: 'bills' }
      ],
      investmentBuckets: []
    }
  },
  {
    id: 'growing_family',
    name: 'Growing Family',
    icon: FiUsers,
    color: 'emerald',
    desc: 'Married, mortgage, kids',
    payload: {
      goals: {
        currentAge: 34,
        targetAge: 62,
        expectedROI: 7,
        startingNetWorth: 120000
      },
      incomeStreams: [
        { id: 1, name: 'Primary Salary', amount: 7500, frequency: 'monthly', accountType: 'income_hub' },
        { id: 2, name: 'Partner Salary', amount: 6000, frequency: 'monthly', accountType: 'income_hub' }
      ],
      expenses: [
        { id: 1, name: 'Mortgage & Tax', amount: 2800, frequency: 'monthly', accountType: 'bills' },
        { id: 2, name: 'Childcare', amount: 1500, frequency: 'monthly', accountType: 'bills' },
        { id: 3, name: 'Groceries', amount: 1000, frequency: 'monthly', accountType: 'bills' },
        { id: 4, name: 'Vehicles/Gas', amount: 800, frequency: 'monthly', accountType: 'bills' },
        { id: 5, name: 'Insurance/Health', amount: 600, frequency: 'monthly', accountType: 'bills' },
        { id: 6, name: 'Discretionary', amount: 1200, frequency: 'monthly', accountType: 'discretionary' }
      ],
      investmentBuckets: [
        { id: 1, name: 'Joint 401ks', amount: 1500, frequency: 'monthly', icon: 'Briefcase', color: 'slate', accountType: 'investing_transfer' },
        { id: 2, name: '529 Plan', amount: 400, frequency: 'monthly', icon: 'Target', color: 'purple', accountType: 'investing_transfer' },
        { id: 3, name: 'Family Vacation', amount: 300, frequency: 'monthly', icon: 'MapPin', color: 'orange', accountType: 'vacation' }
      ]
    }
  },
  {
    id: 'peak_earning',
    name: 'Peak Earning',
    icon: FiActivity,
    color: 'purple',
    desc: 'Established career, high savings',
    payload: {
      goals: {
        currentAge: 45,
        targetAge: 55,
        expectedROI: 7,
        startingNetWorth: 450000
      },
      incomeStreams: [
        { id: 1, name: 'Executive Salary', amount: 14000, frequency: 'monthly', accountType: 'income_hub' },
        { id: 2, name: 'RSUs / Bonus', amount: 3000, frequency: 'monthly', accountType: 'income_hub' }
      ],
      expenses: [
        { id: 1, name: 'Mortgage', amount: 3200, frequency: 'monthly', accountType: 'bills' },
        { id: 2, name: 'Property Tax/Ins', amount: 900, frequency: 'monthly', accountType: 'bills' },
        { id: 3, name: 'Living Expenses', amount: 2500, frequency: 'monthly', accountType: 'bills' },
        { id: 4, name: 'Travel/Leisure', amount: 1500, frequency: 'monthly', accountType: 'vacation' }
      ],
      investmentBuckets: [
        { id: 1, name: 'Max 401k', amount: 1916, frequency: 'monthly', icon: 'Briefcase', color: 'slate', accountType: 'investing_transfer' },
        { id: 2, name: 'Backdoor Roth', amount: 583, frequency: 'monthly', icon: 'Lock', color: 'blue', accountType: 'investing_transfer' },
        { id: 3, name: 'Taxable Brokerage', amount: 4000, frequency: 'monthly', icon: 'TrendingUp', color: 'emerald', accountType: 'investing_transfer' }
      ]
    }
  },
  {
    id: 'pre_retire',
    name: 'Pre-Retirement',
    icon: FiCompass,
    color: 'orange',
    desc: 'Empty nester, wealth preservation',
    payload: {
      goals: {
        currentAge: 58,
        targetAge: 62,
        expectedROI: 5,
        startingNetWorth: 1200000
      },
      incomeStreams: [
        { id: 1, name: 'Salary', amount: 11000, frequency: 'monthly', accountType: 'income_hub' }
      ],
      expenses: [
        { id: 1, name: 'Housing (Paid Off)', amount: 1000, frequency: 'monthly', accountType: 'bills' },
        { id: 2, name: 'Healthcare Premium', amount: 800, frequency: 'monthly', accountType: 'bills' },
        { id: 3, name: 'Living Expenses', amount: 2000, frequency: 'monthly', accountType: 'bills' },
        { id: 4, name: 'Travel', amount: 1200, frequency: 'monthly', accountType: 'vacation' }
      ],
      investmentBuckets: [
        { id: 1, name: 'Catch-up 401k', amount: 2541, frequency: 'monthly', icon: 'Briefcase', color: 'slate', accountType: 'investing_transfer' },
        { id: 2, name: 'HSA (Max)', amount: 345, frequency: 'monthly', icon: 'Shield', color: 'indigo', accountType: 'investing_transfer' },
        { id: 3, name: 'Bonds/Income', amount: 2000, frequency: 'monthly', icon: 'Layers', color: 'blue', accountType: 'investing_transfer' }
      ]
    }
  }
];

export const LIFE_SCENARIOS = BASE_SCENARIOS.map(s => ({
  ...s,
  style: SCENARIO_STYLES[s.color] || SCENARIO_STYLES.blue
}));